import { NetworkCategoryExtended } from '@/types/enums';
import clsx from 'clsx';
import { AnimatePresence, motion, useInView } from 'framer-motion';
import { useMemo, useRef, useState } from 'react';
import { useWindowSize } from 'usehooks-ts';
import { BASE_URL } from '../../../../Config/ApiConfig';
import { Image } from '../../../../components/Image';
import EcosystemBg from './assets/bg.png';
import EcosystemBgMobile from './assets/bgMobile.png';
import { supportedNetworks } from './configs';
import './styles.css';
import { Link } from 'react-router-dom';
import { ROUTES } from '@/constants/routes.constants';

export function SupportedNetworks() {
  const [selectedCategory, setSelectedCategory] =
    useState<NetworkCategoryExtended>(NetworkCategoryExtended.All);
  const targetRef = useRef(null);
  const { width } = useWindowSize();
  const isInView = useInView(targetRef, {
    once: true,
    margin: '-30%',
  });

  const bgImage = useMemo(() => {
    if (width < 992) {
      return EcosystemBgMobile;
    }
    return EcosystemBg;
  }, [width]);

  const filteredNetworks = useMemo(() => {
    if (selectedCategory === NetworkCategoryExtended.All) {
      return supportedNetworks;
    }

    if (selectedCategory === NetworkCategoryExtended.TESTNET) {
      return supportedNetworks.filter(network => network.is_testnet);
    }

    return supportedNetworks.filter(
      network => network.tag === selectedCategory
    );
  }, [selectedCategory]);

  return (
    <div className="main-page--networks" ref={targetRef}>
      <motion.div
        animate={{
          opacity: isInView ? 1 : 0,
        }}
        transition={{ duration: 0.5 }}
        className="main-page--networks-bg"
      >
        <img src={bgImage} alt="" draggable={false} />
      </motion.div>
      <motion.div
        animate={{
          y: isInView ? 0 : 50,
          opacity: isInView ? 1 : 0,
        }}
        transition={{ duration: 0.5 }}
        className="d-flex flex-column flex-lg-row justify-content-between align-items-center"
      >
        <h2 className="main-page--networks-heading">
          Our expanding network of ecosystems
        </h2>
        <div className="main-page--networks-badge-group">
          {Object.values(NetworkCategoryExtended).map(category => (
            <button
              key={category}
              className={clsx(
                'main-page--networks-badge',
                category === selectedCategory && 'active'
              )}
              onClick={() => setSelectedCategory(category)}
            >
              <span className="z-1">{category}</span>
            </button>
          ))}
        </div>
      </motion.div>
      <motion.div
        variants={{
          animate: {
            transition: {
              delayChildren: 0.15,
              staggerChildren: 0.05,
            },
          },
        }}
        animate={isInView ? 'animate' : 'initial'}
        className="main-page--networks-content position-relative"
        id="networks-content"
      >
        <AnimatePresence mode="wait">
          {filteredNetworks.map((network, i) => (
            <Link
              to={
                network.is_coming_soon
                  ? '#'
                  : (network.is_testnet
                      ? ROUTES.TESTNET_SEND_PAGE
                      : ROUTES.SEND_PAGE) + `?destination=${network.name}`
              }
              target={network.is_coming_soon ? '' : '_blank'}
              key={selectedCategory + network.name}
            >
              <motion.div
                transition={{
                  duration: 0.3,
                }}
                initial={{
                  opacity: 0,
                  scale: 0,
                }}
                animate={
                  isInView
                    ? {
                        opacity: 1,
                        scale: 1,
                      }
                    : undefined
                }
                exit={{
                  opacity: 0,
                  scale: 0,
                }}
                className={clsx(
                  'main-page--networks-item',
                  network.is_coming_soon && 'coming-soon',
                  network.is_testnet && 'testnet'
                )}
              >
                <Image
                  withSmoothLoading
                  src={
                    network.network_image_url?.startsWith('http')
                      ? network.network_image_url
                      : BASE_URL + network.network_image_url
                  }
                  alt={network.name}
                  draggable={false}
                  className="main-page--networks-item-logo"
                />
                <span className="main-page--networks-item--name">
                  {network.name}
                  <span className="main-page--networks-item--description">
                    {network.tag}
                  </span>
                </span>
              </motion.div>
            </Link>
          ))}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
